import React from 'react';
import Form from '../../../form';
import { TFormVariant } from '../../../form/Form.types';
import InputAction from './components/input-action';
import InputClearAction from './components/input-clear-action';
import InputIcon from './components/input-icon';
import InputPrefix from './components/input-prefix';
import InputSuffix from './components/input-suffix';
import {
  ISkeletonContext,
  SkeletonContextProvider,
  useSkeletonContext,
} from './input-skeleton.context';
import {
  InputContent,
  InputContentWrapper,
  SkeletonSC,
  LeftAdornmentSC,
  RightAdornmentSC,
} from './input-skeleton.styled';
import hoistNonReactStatistics from 'hoist-non-react-statics';
import classNames from 'classnames';
import InputSkeletonPlaceholder from './components/input-skeleton-placeholder';

export interface InputSkeletonProps
  extends Omit<
    React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    'ref'
  > {
  left?: ((state: ISkeletonContext) => React.ReactNode) | React.ReactNode;
  right?: ((state: ISkeletonContext) => React.ReactNode) | React.ReactNode;
  children: React.ReactNode;
  variant?: TFormVariant;
  /**
   * Input states
   */
  focused?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  error?: boolean;
  /**
   * Handle press on parent
   */
  onClick?: React.HTMLAttributes<HTMLDivElement>['onClick'];
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  leftStyle?: React.CSSProperties;
  rightStyle?: React.CSSProperties;
  borderLess?: boolean;
  className?: string;
  outlined?: boolean;
}

const InputSkeleton = (
  {
    left,
    right,
    children,
    variant,
    focused,
    disabled,
    readonly,
    error,
    onClick,
    style,
    borderLess,
    contentStyle,
    leftStyle,
    rightStyle,
    className,
    outlined,
    ...rest
  }: InputSkeletonProps,
  ref?: React.Ref<HTMLDivElement>,
) => {
  const settings = Form.useFormSettings({ variant, disabled, borderLess });

  const inputState = React.useMemo(
    () => ({ focused, disabled, readonly, error }),
    [focused, disabled, readonly, error],
  );

  return (
    <SkeletonContextProvider value={inputState}>
      <SkeletonSC
        {...rest}
        style={{ ...style, opacity: settings.inForm ? 1 : settings.disabled ? 0.5 : 1 }}
        onClick={onClick}
        tabIndex={!(disabled || readonly) ? undefined : -1}
        className={classNames('input-skeleton', className, settings.variant)}
        focused={focused}
        error={error}
        disabled={disabled}
        ref={ref}
        variant={settings.variant}
        outlined={outlined}
      >
        <InputContentWrapper style={{ opacity: 1 }}>
          {left ? (
            <LeftAdornmentSC style={leftStyle}>
              {typeof left === 'function' ? left(inputState) : left}
            </LeftAdornmentSC>
          ) : null}
          <InputContent
            className="input-skeleton-content"
            style={{
              marginLeft: left ? 0 : 16,
              marginRight: right ? 0 : 16,
              ...contentStyle,
            }}
          >
            {children}
          </InputContent>
          {right ? (
            <RightAdornmentSC style={rightStyle}>
              {typeof right === 'function' ? right(inputState) : right}
            </RightAdornmentSC>
          ) : null}
        </InputContentWrapper>
      </SkeletonSC>
    </SkeletonContextProvider>
  );
};

InputSkeleton.Prefix = InputPrefix;
InputSkeleton.Suffix = InputSuffix;
InputSkeleton.Action = InputAction;
InputSkeleton.ClearAction = InputClearAction;
InputSkeleton.Icon = InputIcon;
InputSkeleton.Placeholder = InputSkeletonPlaceholder;
InputSkeleton.useSkeletonContext = useSkeletonContext;

export default hoistNonReactStatistics(React.forwardRef(InputSkeleton), InputSkeleton);
